import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type Theme,
} from 'ag-grid-community';
import { BRAND } from '@/lib/brand';

/**
 * Register every community-edition AG Grid module exactly once. AG Grid
 * v32+ split features into per-feature modules and yells (`ValidationModule`)
 * if grid options reference a module that wasn't registered. We pay the
 * bundle cost for the full community set here for simplicity — the
 * Projections tabs use enough features (pagination, pinned rows,
 * filters, editors, CSV export) that selective registration wouldn't
 * save meaningful weight.
 *
 * `_atlasAgGridRegistered` is hung off the module-loader cache so HMR
 * reloads and tab switches don't try to re-register and trip the
 * "modules already registered" guard.
 */
declare global {
  var _atlasAgGridRegistered: boolean | undefined;
}

if (!globalThis._atlasAgGridRegistered) {
  ModuleRegistry.registerModules([AllCommunityModule]);
  globalThis._atlasAgGridRegistered = true;
}

/**
 * Atlas brand theme for AG Grid, built off the Quartz preset. We pass
 * brand tokens straight through to AG Grid's theming API so the grid
 * picks up Linen surfaces, Humus text, and Old Growth borders without
 * needing a custom CSS file alongside the shadcn stylesheet.
 *
 * Theming is applied at the React component level via `theme={atlasGridTheme}`
 * on `<AgGridReact>` — never via the legacy `ag-theme-*` CSS class. Mixing
 * the two is what produces the broken double-borders / wrong-font glitches
 * you sometimes see in AG Grid screenshots.
 */
export const atlasGridTheme: Theme = themeQuartz.withParams({
  fontFamily: 'inherit',
  fontSize: 13,
  backgroundColor: '#FFFFFF',
  foregroundColor: BRAND.humus,
  headerBackgroundColor: BRAND.linen,
  headerTextColor: BRAND.oldGrowth,
  headerFontWeight: 500,
  headerFontSize: 11,
  headerHeight: 36,
  rowHeight: 34,
  borderColor: BRAND.linenDark,
  rowBorder: { color: BRAND.linenDark, style: 'solid', width: 1 },
  columnBorder: { color: BRAND.linenDark, style: 'solid', width: 1 },
  oddRowBackgroundColor: '#FAF8F3',
  selectedRowBackgroundColor: '#E8DFCE66',
  rangeSelectionBackgroundColor: '#E8DFCE99',
  rangeSelectionBorderColor: BRAND.humus,
  accentColor: BRAND.humus,
  inputFocusBorder: { color: BRAND.humus, style: 'solid', width: 2 },
  cellHorizontalPadding: 10,
  spacing: 6,
  wrapperBorderRadius: 8,
  wrapperBorder: { color: BRAND.linenDark, style: 'solid', width: 1 },
});
